import type { InterfaceGenericMap } from '@interfaces/InterfaceGenericMap.ts';
import { createSlice } from '@reduxjs/toolkit';
import type { AppState } from '@store/appStore.ts';
import { meLogOut } from '@store/reducers/meReducer.ts';
import { sliceStateCheckAndUpdate } from '@store/reducers/reducerHelpers/sliceStateCheckAndUpdate.ts';
import { thunkDelete } from '@store/reducers/reducerHelpers/thunkDelete.ts';
import { thunkGet } from '@store/reducers/reducerHelpers/thunkGet.ts';
import { thunkPost } from '@store/reducers/reducerHelpers/thunkPost.ts';
import { thunkPut } from '@store/reducers/reducerHelpers/thunkPut.ts';

export interface InterfaceContact {
  id: number;
  companyId: number;
  email: {
    id: number;
    email: string;
    verifiedAt: string | null;
    isChecked: boolean;
    isValid: boolean;
    createdAt: string;
    updatedAt: string;
  };
  phoneNumber: {
    uuid: string;
    countryIsoCode: string;
    phoneNumber: string;
    isChecked: boolean;
    isValid: boolean;
  };
  type: {
    slug: string;
    name: string;
    description: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface ContactsReducerState {
  contacts: InterfaceGenericMap<InterfaceContact>;
}

const initialState: ContactsReducerState = {
  contacts: {},
};

const sliceName = 'contacts';

export const contactsSlice = createSlice({
  name: sliceName,
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(meLogOut.fulfilled, () => initialState)
      .addCase(getCompanyContacts.fulfilled, sliceStateCheckAndUpdate.sliceItem('contacts'))
      .addCase(updateContact.fulfilled, sliceStateCheckAndUpdate.sliceItemElementGeneric('contacts', 'id'))
      .addCase(deleteContact.fulfilled, (state, action) => {
        if (action.meta.arg?.urlVarsReplaceMap) {
          const contactId = action.meta.arg.urlVarsReplaceMap['{contactId}'];
          if (contactId && state.contacts[contactId]) {
            delete state.contacts[contactId];
          }
        }
      });
  },
});

export const getCompanyContacts = thunkGet.compUrl(sliceName, '/c/{companySlug}/admin/contacts');
export const updateContact = thunkPut.compUrl<InterfaceContact>(
  sliceName,
  '/c/{companySlug}/admin/contacts/{contactId}',
);
export const deleteContact = thunkDelete.compUrl(sliceName, '/c/{companySlug}/admin/contacts/{contactId}');
export const createContact = thunkPost.compUrl<Omit<InterfaceContact, 'id'>>(
  sliceName,
  '/c/{companySlug}/admin/contacts',
);

export const selectContacts = (state: AppState) => state.contacts.contacts;
export const selectContact = (contactId: number) => (state: AppState) => state.contacts.contacts[contactId];

export default contactsSlice.reducer;
